import React from "react";
import { connect } from "react-redux";

import Dialog from "../dialog/Dialog";
import DialogContent from "../dialog/DialogContent";
import DialogFooter from "../dialog/DialogFooter";
import DialogOK from "../dialog/DialogOK";
import DialogCancel from "../dialog/DialogCancel";
import { IAppState } from "../../state/Store";
import DialogBad from "../dialog/DialogBad";
import Spacer from "../containers/Spacer";
import { RecycledItem } from "../../types/system/RecycledItem";
import Grid from "../grid/Grid";
import GridColumn, { GridColumnType } from "../grid/GridColumn";
import { AppContext } from "../../types/system/AppContext";
import {
    RecycledType,
    removeFiles,
    restoreFiles,
    setRecycledOpen,
} from "../../state/system/Recycled";

import "./Recycled.scss";
import { unwrappedDispatch } from "../../foundation/Dispatching";

interface RecycledProps {
    open: boolean;
    types: RecycledType[];
    data: RecycledItem[];
    loading: boolean;

    setRecycledOpen: (open: boolean) => void;
    restoreFiles: (items: RecycledItem[]) => Promise<void>;
    removeFiles: (items: RecycledItem[]) => Promise<void>;
}

interface RecycledState {
    selections: RecycledItem[];
}

class Recycled extends React.Component<RecycledProps, RecycledState> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state = {
        selections: [],
    };

    async restoreClick() {
        await this.props.restoreFiles(this.state.selections);
        this.context.refreshRoute();
    }

    async removeClick() {
        await this.props.removeFiles(this.state.selections);
    }

    cancelClick() {
        this.props.setRecycledOpen(false);
    }

    render() {
        return (
            <Dialog
                dialogIcon="delete"
                dialogTitle="Recycle Bin"
                showDialog={this.props.open}
                showLoading={this.props.loading}
                width={800}
                zIndex={997}>
                <DialogContent>
                    <Grid<RecycledItem>
                        value={this.props.data}
                        fullWidth={true}
                        fixedHeader={true}
                        maxHeight="400px"
                        keyField="id"
                        bulkEdit={true}
                        defaultSortField="number"
                        selectionChanged={(_, selections) =>
                            this.setState({ selections })
                        }>
                        <GridColumn
                            header="ID"
                            field="id"
                            selectable={true}
                        />
                        <GridColumn
                            header="Code"
                            field="code"
                            selectable={true}
                        />
                        <GridColumn
                            header="Name"
                            field="name"
                            selectable={true}
                        />
                        <GridColumn
                            header="Recycled On"
                            field="recycledOn"
                            selectable={true}
                            type={GridColumnType.Date}
                            includeTime={true}
                        />
                        <GridColumn
                            header="Recycled By"
                            field="recycledBy"
                            selectable={true}
                        />
                    </Grid>
                </DialogContent>
                <DialogFooter full={true}>
                    <DialogBad
                        text="Remove"
                        disabled={!this.state.selections.length}
                        onClick={() => this.removeClick()}
                    />

                    <Spacer />

                    <DialogCancel onClick={() => this.cancelClick()} />
                    <DialogOK
                        text="Restore"
                        disabled={!this.state.selections.length}
                        onClick={() => this.restoreClick()}
                    />
                </DialogFooter>
            </Dialog>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { recycled } = state.db;
    return {
        open: recycled.open,
        types: recycled.types,
        data: recycled.data,
        loading: recycled.loading,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setRecycledOpen: (open: boolean) => {
            dispatch(setRecycledOpen(open));
        },
        restoreFiles: async (items: RecycledItem[]) => {
            await unwrappedDispatch(restoreFiles(items));
        },
        removeFiles: async (items: RecycledItem[]) => {
            await unwrappedDispatch(removeFiles(items));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Recycled);
